import { useCallback } from 'react';

import { useSession } from './useSession';
import {
  createPasskeySigner,
  registerPasskey,
  authenticatePasskey,
  isPasskeySupported,
  arrayBufferToBase64Url,
  type RegistrationResponseJSON,
} from '@wafra/passkey';
import { apiClient } from '~/lib/api-client';
import { useQuery } from '@tanstack/react-query';
import { useMutation } from '@tanstack/react-query';

export interface PasskeyAuthenticationResponse {
  passkeyId: string;
  credentialId: string;
  publicKey: string;
  rawId: string;
  credential: {
    publicKey: string;
    credentialId: string;
    rawId: string;
    response: {
      authenticatorData: Uint8Array;
      clientDataJSON: Uint8Array;
      signature: Uint8Array;
      userHandle: Uint8Array;
    };
  };
}

interface PasskeyInfo {
  id: string;
  credentialId: string;
  publicKey: string;
  deviceName: string;
  deviceType: string;
  approved: boolean;
  address: string;
  createdAt: string;
  lastUsed: string;
}

interface PendingApprovalInfo {
  id: string;
  passkeyId: string;
  deviceName: string;
  deviceType: string;
  requestedAt: Date;
  status: "pending" | "approved" | "rejected";
}


interface ChallangeResponse {
  challenge: PublicKeyCredentialJSON & {
    authenticatorAttachment: 'internal' | 'external';
    getClientExtensionResults: () => {
      appid: boolean;
    };
    toJSON: () => string;
  };
  challengeId: string;
}

export const authenticateWithPasskey = async (): Promise<PasskeyAuthenticationResponse> => {
  try {
    const authResponse = await apiClient.post<ChallangeResponse>('passkey.generateAuthenticationChallenge');
    // Get authentication options from the server using tRPC
    const { challenge: challengeData, challengeId } = authResponse;

    // The challenge field is already decoded, no need to decode again
    const decodedChallenge = challengeData;

    // We need to call the raw WebAuthn API to get the actual credential response
    // instead of using the abstracted authenticatePasskey function
    if (typeof navigator === 'undefined' || !navigator.credentials) {
      throw new Error('WebAuthn not supported in this environment');
    }

    // Convert base64url challenge to ArrayBuffer
    const challenge = new TextEncoder().encode(decodedChallenge.challenge);

    const publicKeyOptions: PublicKeyCredentialRequestOptions = {
      challenge,
      rpId: decodedChallenge.rpId,
      allowCredentials: decodedChallenge.allowCredentials?.map((cred: any) => ({
        id: new TextEncoder().encode(cred.id),
        type: cred.type as PublicKeyCredentialType,
        transports: ['internal'] as AuthenticatorTransport[],
      })),
      userVerification: decodedChallenge.userVerification || 'preferred',
      timeout: 60000,
    };

    const credential = (await navigator.credentials.get({
      publicKey: publicKeyOptions,
    })) as PublicKeyCredential & {
      response: AuthenticatorAssertionResponse & {
        authenticatorData: Uint8Array;
      };
      publicKey: string;
    };

    if (!credential) {
      throw new Error('Passkey authentication was cancelled');
    }

    // Format the credential for the server (this is the raw WebAuthn response)
    const credentialResponse = {
      ...credential,
      rawId: credential.id,
      credentialId: credential.id,
      publicKey: credential.publicKey,
      response: {
        authenticatorData: new Uint8Array(credential.response.authenticatorData),
        clientDataJSON: new Uint8Array(credential.response.clientDataJSON),
        signature: new Uint8Array(
          (credential.response as AuthenticatorAssertionResponse).signature
        ),
        userHandle: (credential.response as AuthenticatorAssertionResponse).userHandle!,
      },
    };

    // Send the raw credential response to the server for verification using tRPC
    const verificationResponse = await apiClient.post<PasskeyAuthenticationResponse>('passkey.verifyAuthentication', {
      challengeId,
      credential: credentialResponse,
    });

    // @ts-ignore
    return { ...verificationResponse, credential: credentialResponse };
  } catch (error) {
    console.error('Passkey authentication failed:', error);
    throw error;
  }
};

export function usePasskey() {
  const { session, status } = useSession();

  // Only run authenticated queries when user is fully authenticated
  const isAuthenticated =
    status === 'authenticated' && !!session && !!session.user?.onboardingCompleted;

  const generateRegistrationChallengeMutation = useMutation({
    mutationFn: () => apiClient.post<ChallangeResponse>('passkey.generateRegistrationChallenge'),
  });
  const verifyRegistrationMutation = useMutation({
    mutationFn: (data: {
      challengeId: string;
      credential: {
        id: string;
        publicKey: string;
        response: {
          attestationObject: string;
          clientDataJSON: string;
        };
      }; deviceName: string; deviceType: string
    }) => apiClient.post<PasskeyAuthenticationResponse>('passkey.verifyRegistration', data),
  });
  const generateTransactionSigningChallengeMutation = useMutation({
    mutationFn: () => apiClient.post<ChallangeResponse>('passkey.generateTransactionSigningChallenge'),
  });
  const listPasskeysQuery = useQuery({
    queryKey: ['passkeys'],
    queryFn: () => apiClient.get<{
      count: number;
      passkeys: PasskeyInfo[];
    }>('passkey.listPasskeys'),
    enabled: isAuthenticated,
  });
  const getPendingApprovalsQuery = useQuery({
    queryKey: ['pendingApprovals'],
    queryFn: () => apiClient.get<{
      count: number;
      requests: PendingApprovalInfo[];
    }>('passkey.getPendingApprovals'),
    enabled: isAuthenticated,
  });
  const getMyPasskeyQuery = useQuery({
    queryKey: ['myPasskey'],
    queryFn: () => apiClient.get<PasskeyInfo>('passkey.getMyPasskey'),
    enabled: isAuthenticated,
  });
  const rejectApprovalMutation = useMutation({
    mutationFn: (data: { requestId: string }) => apiClient.post<PasskeyAuthenticationResponse>('passkey.rejectApproval', data),
  });

  const registerPasskeyHandler = useCallback(
    async (deviceInfo?: any): Promise<any> => {
      try {
        // Get registration options from the server using tRPC
        const registrationResponse = await generateRegistrationChallengeMutation.mutateAsync();
        const { challenge: challengeData, challengeId } = registrationResponse;

        // The challenge field is already an object with the decoded data
        const decodedChallenge = challengeData;

        // Use platform-agnostic registration
        const result = await registerPasskey({
          challenge: decodedChallenge.challenge,
          rpName: decodedChallenge.rp.name,
          rpID: decodedChallenge.rp.id,
          userID: decodedChallenge.user.id,
          userName: decodedChallenge.user.name,
          excludeCredentials: decodedChallenge.excludeCredentials,
        });

        // Use getCredentialResponse() from registerPasskey result
        const credential = result.getCredentialResponse() as RegistrationResponseJSON & {
          publicKey: string;
        };

        // Send the attestation response to the server for verification
        const verificationResponse = await verifyRegistrationMutation.mutateAsync({
          challengeId,
          credential,
          deviceName: deviceInfo?.deviceName || deviceInfo?.osName || 'Unknown',
          deviceType: deviceInfo?.deviceType?.toLowerCase() || 'desktop',
        });

        // No more needsApproval, just use approved
        const signer = await createPasskeySigner({
          publicKey: credential.publicKey,
          credentialId: result.credentialId,
        });
        if (typeof window !== 'undefined') {
          localStorage.setItem(
            'passkey-' + verificationResponse.credentialId,
            JSON.stringify(signer)
          );
        }

        return verificationResponse;
      } catch (error: any) {
        if (error.message.includes('excludeCredentials')) {
          throw new Error('The passkey is already registered with an account.');
        }
        if (error.message.includes('The operation either timed out or was not allowed')) {
          throw new Error('Please try again.');
        }
        console.error('Passkey registration failed:', error);
        throw error;
      }
    },
    [generateRegistrationChallengeMutation, verifyRegistrationMutation]
  );

  const listPasskeys = useCallback(async () => {
    try {
      const result = await listPasskeysQuery.refetch();
      return result.data?.passkeys || [];
    } catch (error) {
      console.error('Failed to list passkeys:', error);
      throw error;
    }
  }, [listPasskeysQuery]);

  const getPendingApprovalRequests = useCallback(async () => {
    try {
      const result = await getPendingApprovalsQuery.refetch();
      return result.data;
    } catch (error) {
      console.error('Failed to get pending approval requests:', error);
      throw error;
    }
  }, [getPendingApprovalsQuery]);

  const getSessionPasskey = useCallback(async () => {
    try {
      const result = await getMyPasskeyQuery.refetch();
      return result.data;
    } catch (error) {
      console.error('Failed to check approval status:', error);
      throw error;
    }
  }, [getMyPasskeyQuery]);

  const rejectDeviceRequest = useCallback(
    async (passkeyToReject: any) => {
      try {
        return await rejectApprovalMutation.mutateAsync({
          requestId: passkeyToReject.id,
        });
      } catch (error) {
        console.error('Failed to reject device request:', error);
        throw error;
      }
    },
    [rejectApprovalMutation]
  );

  // Used in Safe signing flow
  const getCredentials = useCallback(
    async (options?: CredentialRequestOptions) => {
      // Use the transaction signing challenge endpoint which requires authentication
      // and properly limits allowCredentials to the current session passkey
      const authResponse = await generateTransactionSigningChallengeMutation.mutateAsync();
      const { challenge: optionsFromServer } = authResponse;

      const challenge = arrayBufferToBase64Url(options!.publicKey!.challenge! as ArrayBuffer);

      // Use passkey package for authentication
      const result = await authenticatePasskey({
        challenge,
        rpID: optionsFromServer.rpId,
        allowCredentials: optionsFromServer.allowCredentials,
        userVerification:
          options?.publicKey?.userVerification ?? optionsFromServer.userVerification,
      });

      // Return the raw credential for Safe signing
      return result.rawCredential;
    },
    [generateTransactionSigningChallengeMutation]
  );

  const getPasskeySigner = useCallback(async () => {
    // Check if we are using the same passkey of the session to sign
    const passkey = await getSessionPasskey();

    if (!passkey) {
      throw new Error('No passkey found for the current session');
    }

    // Attach getCredentials as getFn, but do NOT call it here
    return await createPasskeySigner(
      {
        publicKey: passkey.publicKey,
        credentialId: passkey.credentialId,
      },
      getCredentials // pass the function reference, do not call it
    );
  }, [getCredentials, getSessionPasskey]);

  return {
    registerPasskey: registerPasskeyHandler,
    listPasskeys,
    getPasskeySigner,
    getPendingApprovalRequests,
    getSessionPasskey,
    rejectDeviceRequest,
    isPasskeySupported,
    // Expose loading states (only when actively fetching)
    isLoading:
      listPasskeysQuery.isFetching ||
      getPendingApprovalsQuery.isFetching ||
      getMyPasskeyQuery.isFetching,
  };
}
